import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/useAuth";

export interface TrailCompletion {
  trail_id: string;
  trail_name: string;
  total_segments: number;
  completed_segments: number;
  total_length_miles: number;
  completed_length_miles: number;
  completion_percentage: number;
  last_completed_at: string | null;
}

export interface EnhancedUserMetrics {
  totalMiles: number;
  totalHours: number;
  totalRuns: number;
  trailsCompleted: TrailCompletion[];
  trailsCompletedCount: number;
  lastRun: {
    id: string;
    created_at: string;
    title: string;
    relativeTime: string;
  } | null;
  activeMonthsThisYear: {
    count: number;
    total: number;
    hasStreak: boolean;
    streakLength: number;
  };
  photosShared: number;
  waypointsShared: number;
  totalViewsReceived: number;
}

export function useEnhancedUserMetrics(userId: string | undefined) {
  const { user } = useAuth();
  const canReadPrivateViewMetrics = user?.id === userId;

  return useQuery({
    queryKey: ["enhanced-user-metrics", userId, canReadPrivateViewMetrics],
    queryFn: async (): Promise<EnhancedUserMetrics> => {
      if (!userId) throw new Error("User ID is required");

      const { data: runIds } = await supabase
        .from("runs")
        .select("id")
        .eq("user_id", userId);

      const runIdList = runIds?.map((r) => r.id) || [];

      const [
        runsData,
        userSegmentsData,
        trailSegmentsData,
        monthlyActivityData,
        photosData,
        waypointsData,
        viewsData,
      ] = await Promise.all([
        supabase
          .from("runs")
          .select("id, created_at, start_time, title, distance_miles, duration_minutes, photo_urls")
          .eq("user_id", userId)
          .order("start_time", { ascending: false, nullsFirst: false }),

        runIdList.length > 0
          ? supabase
              .from("run_segments")
              .select("trail_segment_id, run_id")
              .in("run_id", runIdList)
          : Promise.resolve({ data: [], error: null }),

        supabase
          .from("trail_segments")
          .select("id, trail_id, length_miles"),

        supabase
          .from("runs")
          .select("created_at, start_time")
          .eq("user_id", userId)
          .gte(
            "created_at",
            new Date(new Date().getFullYear(), 0, 1).toISOString()
          ),

        supabase
          .from("runs")
          .select("photo_urls")
          .eq("user_id", userId)
          .not("photo_urls", "is", null),

        runIdList.length > 0
          ? supabase
              .from("run_waypoints")
              .select("id", { count: "exact", head: true })
              .in("run_id", runIdList)
          : Promise.resolve({ count: 0, error: null }),

        canReadPrivateViewMetrics && runIdList.length > 0
          ? supabase
              .from("run_views")
              .select("id", { count: "exact", head: true })
              .in("run_id", runIdList)
          : Promise.resolve({ count: 0, error: null }),
      ]);

      if (runsData.error) throw runsData.error;
      if (userSegmentsData.error) throw userSegmentsData.error;
      if (trailSegmentsData.error) throw trailSegmentsData.error;
      if (monthlyActivityData.error) throw monthlyActivityData.error;
      if (photosData.error) throw photosData.error;
      if (waypointsData.error) throw waypointsData.error;
      if (viewsData.error) throw viewsData.error;

      const runs = runsData.data || [];
      const userSegments = userSegmentsData.data || [];
      const trailSegments = trailSegmentsData.data || [];

      const totalMiles = runs.reduce(
        (sum, run) => sum + (run.distance_miles || 0),
        0
      );
      const totalMinutes = runs.reduce(
        (sum, run) => sum + (run.duration_minutes || 0),
        0
      );
      const totalHours = Math.round((totalMinutes / 60) * 10) / 10;
      const totalRuns = runs.length;

      const uniqueTrailIds = [...new Set(trailSegments.map((ts: any) => ts.trail_id))].filter(Boolean);
      const { data: trailsData } = uniqueTrailIds.length > 0
        ? await supabase
            .from("trails")
            .select("id, name")
            .in("id", uniqueTrailIds)
        : { data: [] };

      const trailNamesMap = new Map(
        (trailsData || []).map((t: any) => [t.id, t.name])
      );

      const runStartTimeMap = new Map(
        runs.map((r) => [r.id, r.start_time || r.created_at])
      );

      const completedSegmentIds = new Set(
        userSegments.map((us: any) => us.trail_segment_id)
      );

      const segmentCompletionDates = new Map<string, string>();
      userSegments.forEach((us: any) => {
        const runDate = runStartTimeMap.get(us.run_id);
        if (runDate) {
          const existing = segmentCompletionDates.get(us.trail_segment_id);
          if (!existing || runDate > existing) {
            segmentCompletionDates.set(us.trail_segment_id, runDate);
          }
        }
      });

      const trailMap = new Map<string, {
        trail_id: string;
        trail_name: string;
        total_segments: number;
        completed_segments: number;
        total_length_miles: number;
        completed_length_miles: number;
        last_completed_at: string | null;
      }>();

      trailSegments.forEach((segment: any) => {
        const trailId = segment.trail_id;
        if (!trailId) return;

        const trailName = trailNamesMap.get(trailId) || "Unknown Trail";
        const isCompleted = completedSegmentIds.has(segment.id);
        const completedDate = segmentCompletionDates.get(segment.id);

        if (!trailMap.has(trailId)) {
          trailMap.set(trailId, {
            trail_id: trailId,
            trail_name: trailName,
            total_segments: 0,
            completed_segments: 0,
            total_length_miles: 0,
            completed_length_miles: 0,
            last_completed_at: null,
          });
        }

        const trail = trailMap.get(trailId)!;
        trail.total_segments++;
        trail.total_length_miles += segment.length_miles || 0;

        if (isCompleted) {
          trail.completed_segments++;
          trail.completed_length_miles += segment.length_miles || 0;

          if (
            completedDate &&
            (!trail.last_completed_at || completedDate > trail.last_completed_at)
          ) {
            trail.last_completed_at = completedDate;
          }
        }
      });

      const trailsCompleted: TrailCompletion[] = Array.from(trailMap.values())
        .filter((trail) => {
          const completionPct =
            trail.total_length_miles > 0
              ? trail.completed_length_miles / trail.total_length_miles
              : 0;
          return completionPct >= 0.8;
        })
        .map((trail) => ({
          ...trail,
          completion_percentage:
            trail.total_length_miles > 0
              ? (trail.completed_length_miles / trail.total_length_miles) * 100
              : 0,
        }));

      const lastRun = runs[0]
        ? {
            id: runs[0].id,
            created_at: runs[0].created_at,
            title: runs[0].title || "Untitled Run",
            relativeTime: formatDistanceToNow(
              new Date(runs[0].start_time || runs[0].created_at),
              { addSuffix: true }
            ),
          }
        : null;

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const monthlyRuns = monthlyActivityData.data || [];

      const activeMonths = new Set<number>();
      monthlyRuns.forEach((run) => {
        const date = new Date(run.start_time || run.created_at);
        if (date.getFullYear() === currentYear) {
          activeMonths.add(date.getMonth() + 1);
        }
      });

      let streakLength = 0;
      let hasStreak = false;
      if (activeMonths.size > 0) {
        const sortedMonths = Array.from(activeMonths).sort((a, b) => b - a);
        streakLength = 1;
        for (let i = 0; i < sortedMonths.length - 1; i++) {
          if (sortedMonths[i] - sortedMonths[i + 1] === 1) {
            streakLength++;
          } else {
            break;
          }
        }
        hasStreak = streakLength >= 3;
      }

      const photosShared = (photosData.data || []).reduce((sum, run) => {
        const photoUrls = run.photo_urls as string[] | null;
        return sum + (photoUrls ? photoUrls.length : 0);
      }, 0);

      const waypointsShared = waypointsData.count || 0;
      const totalViewsReceived = viewsData.count || 0;

      return {
        totalMiles: Math.round(totalMiles * 10) / 10,
        totalHours,
        totalRuns,
        trailsCompleted,
        trailsCompletedCount: trailsCompleted.length,
        lastRun,
        activeMonthsThisYear: {
          count: activeMonths.size,
          total: currentMonth,
          hasStreak,
          streakLength,
        },
        photosShared,
        waypointsShared,
        totalViewsReceived,
      };
    },
    enabled: !!userId,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
